import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { api } from '../lib/api';

interface User {
  id: string;
  email: string;
  name: string | null;
  avatar_url: string | null;
  email_verified: boolean;
  notification_frequency: 'hourly' | 'six_hourly' | 'daily';
  change_threshold: number;
  tier: 'free' | 'starter' | 'professional';
  max_devices: number;
  is_admin: boolean;
  auth_provider: 'email' | 'google';
  created_at: string;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: () => void;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchUser = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await api.get<{ success: boolean; data: User }>('/api/auth/me');
      if (response.success && response.data) {
        setUser(response.data);
      } else {
        // Token invalid, try refresh
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          const refreshResponse = await api.post<{
            success: boolean;
            data: { token: string; refresh_token: string };
          }>('/api/auth/refresh', { refresh_token: refreshToken });

          if (refreshResponse.success && refreshResponse.data) {
            localStorage.setItem('token', refreshResponse.data.token);
            localStorage.setItem('refreshToken', refreshResponse.data.refresh_token);
            // Retry fetching user
            const retryResponse = await api.get<{ success: boolean; data: User }>('/api/auth/me');
            if (retryResponse.success && retryResponse.data) {
              setUser(retryResponse.data);
            }
          } else {
            // Refresh failed, clear tokens
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
          }
        } else {
          localStorage.removeItem('token');
        }
      }
    } catch (error) {
      console.error('Auth error:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUser();
  }, []);

  const login = () => {
    // Redirect to Google OAuth
    window.location.href = '/api/auth/google?redirect=/dashboard';
  };

  const logout = async () => {
    const refreshToken = localStorage.getItem('refreshToken');
    try {
      await api.post('/api/auth/logout', { refresh_token: refreshToken });
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    setUser(null);
  };

  const refreshUser = async () => {
    await fetchUser();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
